import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useSyncExternalStore } from "react";

import { Button } from "~/components/ui/button";

import { useOperatorsAuth } from "../../../operatorsAuth";
import { DASHBOARDS_CATALOG_BASE_PATH } from "../catalog";
import { ArtifactActions } from "./ArtifactActions";
import {
  ArtifactAssistantWorkspace,
  type ArtifactDashboardSlots,
} from "./assistant/ArtifactAssistantWorkspace";
import { useDashboardAssistantSource } from "./assistant/dashboardAssistantSource";
import { DashboardArtifactRenderer, DashboardArtifactSkeleton } from "./DashboardArtifactRenderer";
import { decodeArtifactParameterSearch, encodeArtifactParameterSearch } from "./artifactParameters";
import {
  DashboardArtifactRequestError,
  dashboardArtifactQueryOptions,
} from "./artifactsReactQuery";

type ArtifactDashboardPageProps = {
  slug: string;
  /** Hide the assistant and editing controls on dashboards intended for viewing and export. */
  showAuthoringControls?: boolean;
};

/** `?version=N` pins a version; without it the page shows what viewers see. */
export function artifactVersionFromSearch(search: string): number | undefined {
  const raw = new URLSearchParams(search).get("version");
  if (raw === null || !/^[1-9]\d*$/.test(raw)) return undefined;
  return Number(raw);
}

/**
 * Reads the raw search string: filters use repeated `p.<id>` keys, which the
 * router's JSON search parsing would rewrite.
 */
function useRawSearch(): [string, (search: URLSearchParams, push?: boolean) => void] {
  const router = useRouter();
  const search = useSyncExternalStore(
    useCallback((onChange: () => void) => router.history.subscribe(onChange), [router]),
    () => router.history.location.search,
  );
  const setSearch = useCallback(
    (next: URLSearchParams, push = false) => {
      const query = next.toString();
      const href = `${router.history.location.pathname}${query ? `?${query}` : ""}`;
      if (push) router.history.push(href);
      else router.history.replace(href);
    },
    [router],
  );
  return [search, setSearch];
}

/**
 * A dashboard built in chat, at `/dashboards/custom/<slug>`, with the
 * assistant beside it when one is provided.
 */
export function ArtifactDashboard(
  props: ArtifactDashboardPageProps & { viewerEmail: string | null },
) {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const assistant = useDashboardAssistantSource();
  const [search, setSearch] = useRawSearch();
  const version = artifactVersionFromSearch(search);
  const query = useQuery(dashboardArtifactQueryOptions(props.slug, version));

  if (query.isPending) return <DashboardArtifactSkeleton className="px-6 py-6" />;
  if (query.error) {
    const missing =
      query.error instanceof DashboardArtifactRequestError && query.error.status === 404;
    return (
      <div className="flex flex-col items-start gap-3 px-6 py-10">
        <h1 className="text-lg font-semibold text-foreground">
          {missing ? "This dashboard does not exist" : "This dashboard could not load"}
        </h1>
        <p role="alert" className="max-w-xl text-sm text-muted-foreground">
          {missing ? "It may have been archived, or the link has a typo." : query.error.message}
        </p>
        <Button render={<Link to={DASHBOARDS_CATALOG_BASE_PATH} />} variant="outline">
          All dashboards
        </Button>
      </div>
    );
  }

  const { artifact, spec } = query.data;
  const selectVersion = (next: number | undefined, push = true) => {
    const params = new URLSearchParams(router.history.location.search);
    if (next === undefined) params.delete("version");
    else params.set("version", String(next));
    setSearch(params, push);
  };
  const versionHref = (next: number) => {
    const params = new URLSearchParams(search);
    params.set("version", String(next));
    return `${router.history.location.pathname}?${params}`;
  };
  /** Loads a version first, so the page swaps to it in place instead of showing a skeleton. */
  const showVersion = async (next: number) => {
    await queryClient.fetchQuery(dashboardArtifactQueryOptions(props.slug, next));
    selectVersion(next, false);
  };

  const renderDashboard = (slots?: ArtifactDashboardSlots) => (
    <DashboardArtifactRenderer
      className="px-6 py-6"
      actions={
        props.showAuthoringControls === false ? undefined : (
          <>
            {slots?.askButton}
            <ArtifactActions
              artifact={artifact}
              version={query.data.version}
              viewerEmail={props.viewerEmail}
              onSelectVersion={selectVersion}
              onRename={slots?.widgetEditor?.renameDashboard}
              onDeleted={() => void navigate({ to: DASHBOARDS_CATALOG_BASE_PATH })}
            />
          </>
        )
      }
      artifact={artifact}
      spec={spec}
      version={query.data.version}
      highlightedWidgetIds={slots?.highlightedWidgetIds}
      emptyNotice={slots?.emptyNotice}
      hidePublishedBanner={props.showAuthoringControls === false}
      widgetEditor={slots?.widgetEditor}
      previews={slots?.previews}
      parameterValues={decodeArtifactParameterSearch(spec.parameters, search)}
      onParameterValuesChange={(values) =>
        setSearch(
          encodeArtifactParameterSearch(spec.parameters, values, new URLSearchParams(search)),
        )
      }
    />
  );

  if (!assistant || props.showAuthoringControls === false) return renderDashboard();
  return (
    <ArtifactAssistantWorkspace
      artifact={artifact}
      renderDashboard={renderDashboard}
      showVersion={showVersion}
      source={assistant}
      shownVersion={query.data.version.version}
      spec={spec}
      versionHref={versionHref}
      viewerEmail={props.viewerEmail}
    />
  );
}

export function ArtifactDashboardPage(props: ArtifactDashboardPageProps) {
  const { user } = useOperatorsAuth();
  return <ArtifactDashboard {...props} viewerEmail={user?.email ?? null} />;
}
